<template>
    <div class="ds-urv-qr-screen">
        <div class="ds-urv-shop">
            <h1 class="ds-urv-shop-name">{{ department.name }}</h1>
            <p class="ds-urv-date">{{ date }}</p>
            <p class="ds-urv-time">{{ time }}</p>
        </div>

        <div class="ds-urv-qr-content">
            <div class="ds-urv-qr-card">
                <h2 class="ds-urv-qr-title">Отсканируйте QR-код</h2>
                <p class="ds-urv-qr-description">Отсканируйте код, чтобы отметиться на приход или уход со смены.</p>
                <div v-if="qrSvg" class="ds-urv-qr-code" aria-label="QR-код подразделения" v-html="qrSvg"></div>
                <div v-else class="ds-urv-loading">
                    {{ isQrLoading ? 'Загружаем QR-код…' : 'QR-код пока недоступен' }}
                </div>
                <p v-if="qrError" class="ds-urv-load-error" role="alert">
                    {{ qrError }}
                    <button type="button" @click="loadQr">Повторить</button>
                    <button type="button" @click="emit('request-key')">Ввести другой ключ</button>
                </p>
                <p class="ds-urv-qr-help">Если возникли трудности со сканированием, обратитесь в техническую поддержку.</p>
            </div>

            <aside class="ds-urv-employees" aria-label="Сотрудники">
                <div v-if="isEmployeesLoading && !workingEmployees.length && !waitingEmployees.length" class="ds-urv-loading">
                    Загружаем сотрудников…
                </div>
                <template v-else>
                    <UrvEmployeeList title="На смене" :employees="workingEmployees" />
                    <UrvEmployeeList title="Ждём на смене" :employees="waitingEmployees" />
                </template>
                <p v-if="employeesError" class="ds-urv-load-error" role="alert">
                    {{ employeesError }}
                    <button type="button" @click="loadEmployees">Повторить</button>
                    <button type="button" @click="emit('request-key')">Ввести другой ключ</button>
                </p>
            </aside>
        </div>
    </div>
    <btn class="ds-urv-return ds-urv-return-absolute" @click="emit('close')">
        Вернуться к расписанию
    </btn>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { getUrvEmployees, getUrvQrSvg, UrvApiError, type UrvDepartment, type UrvWorker } from '@/api/urv'
import { useAuthStore } from '@/stores/authStore'
import UrvEmployeeList from './UrvEmployeeList.vue'

const props = defineProps<{
    department: UrvDepartment
}>()

const defaultQrUpdateInterval = 30_000
const authStore = useAuthStore()
const date = ref('')
const time = ref('')
const qrSvg = ref('')
const qrError = ref('')
const isQrLoading = ref(true)
const workingEmployees = ref<UrvWorker[]>([])
const waitingEmployees = ref<UrvWorker[]>([])
const employeesError = ref('')
const isEmployeesLoading = ref(true)
let dateTimerId: ReturnType<typeof setInterval> | undefined
let qrTimerId: ReturnType<typeof setInterval> | undefined
let employeesTimerId: ReturnType<typeof setInterval> | undefined
let isQrRequestInFlight = false
let isEmployeesRequestInFlight = false

function getQrUpdateInterval() {
    const settings: unknown = authStore.settings

    if (!settings || typeof settings !== 'object') {
        return defaultQrUpdateInterval
    }

    const qrUpdateTime = (settings as Record<string, unknown>).QR_UPDATE_TIME
    const qrUpdateTimeInSeconds = typeof qrUpdateTime === 'number'
        ? qrUpdateTime
        : typeof qrUpdateTime === 'string' ? Number(qrUpdateTime) : Number.NaN

    return Number.isFinite(qrUpdateTimeInSeconds) && qrUpdateTimeInSeconds > 0
        ? qrUpdateTimeInSeconds * 1_000
        : defaultQrUpdateInterval
}

const updateDateTime = () => {
    const now = new Date()
    date.value = new Intl.DateTimeFormat('ru-RU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(now)
    time.value = new Intl.DateTimeFormat('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(now)
}

const emit = defineEmits<{
    (event: 'close'): void
    (event: 'session-invalid'): void
    (event: 'request-key'): void
}>()

async function loadQr() {
    if (isQrRequestInFlight) return

    isQrRequestInFlight = true
    isQrLoading.value = true
    qrError.value = ''

    try {
        qrSvg.value = await getUrvQrSvg(props.department.id)
    } catch (error) {
        handleRequestError(error, (message) => {
            qrError.value = message
        })
    } finally {
        isQrLoading.value = false
        isQrRequestInFlight = false
    }
}

async function loadEmployees() {
    if (isEmployeesRequestInFlight) return

    isEmployeesRequestInFlight = true
    isEmployeesLoading.value = true
    employeesError.value = ''

    try {
        const employees = await getUrvEmployees(props.department.id)
        workingEmployees.value = employees.on_shift
        waitingEmployees.value = employees.waiting_for
    } catch (error) {
        handleRequestError(error, (message) => {
            employeesError.value = message
        })
    } finally {
        isEmployeesLoading.value = false
        isEmployeesRequestInFlight = false
    }
}

function handleRequestError(error: unknown, setError: (message: string) => void) {
    if (error instanceof UrvApiError && error.isSessionInvalid) {
        emit('session-invalid')
        return
    }

    setError(error instanceof Error ? error.message : 'Не удалось получить данные')
}

onMounted(() => {
    updateDateTime()
    loadQr()
    loadEmployees()
    dateTimerId = setInterval(updateDateTime, 1_000)
    qrTimerId = setInterval(loadQr, getQrUpdateInterval())
    employeesTimerId = setInterval(loadEmployees, 60_000)
})

onBeforeUnmount(() => {
    if (dateTimerId !== undefined) clearInterval(dateTimerId)
    if (qrTimerId !== undefined) clearInterval(qrTimerId)
    if (employeesTimerId !== undefined) clearInterval(employeesTimerId)
})
</script>
